<template>
  <div class="category-attribute-editor" :class="{ 'is-embedded': embedded }">
    <div v-if="showTip" class="editor-tip">{{ globalMode ? '配置保存为系统预设属性；普通店铺需确认后同步到自己的模板。' : '配置类目属性后，当前搬家模板下的商品默认使用这些属性值。' }}</div>
    <div class="attribute-layout" :style="{ height }">
      <aside class="category-panel">
        <el-input v-model="keyword" clearable placeholder="搜索类目名称/微信分类ID" prefix-icon="el-icon-search" @input="queueLoadCategories" />
        <div class="filter-tabs">
          <button v-for="tab in tabs" :key="tab.value" type="button" :class="{ active: configured === tab.value }" @click="changeTab(tab.value)">{{ tab.label }}</button>
        </div>
        <div v-loading="categoriesLoading" class="category-list">
          <button
            v-for="item in categories"
            :key="item.category_id"
            type="button"
            class="category-item"
            :class="{ active: Number(item.category_id) === Number(activeCategory.category_id) }"
            @click="selectCategory(item)"
          >
            <span class="category-path">{{ categoryPath(item) }}</span>
            <i v-if="item.is_configured" class="el-icon-success configured-icon" />
          </button>
          <el-empty v-if="!categoriesLoading && !categories.length" :image-size="70" description="暂无符合条件的类目" />
        </div>
      </aside>
      <section class="property-panel">
        <div class="property-header">
          <div>
            <strong>当前类目：</strong>{{ categoryPath(activeCategory) || '请选择左侧类目' }}
            <span v-if="activeCategory.category_id" class="category-id">ID：{{ activeCategory.category_id }}</span>
          </div>
          <span>{{ globalMode ? '系统预设已配置' : '当前模板已配置' }} {{ configuredCount }} 个类目</span>
        </div>
        <div v-loading="propertiesLoading" class="property-list">
          <el-alert v-if="propertyError" type="warning" :title="propertyError" :closable="false" show-icon />
          <el-empty v-else-if="!activeCategory.category_id" description="请先选择需要配置的叶子类目" />
          <el-empty v-else-if="!propertiesLoading && !properties.length" description="该类目暂无可配置属性" />
          <template v-else>
            <div v-if="properties.length" class="property-section-title">类目属性：</div>
            <div v-for="property in properties" :key="property.name" class="property-row">
              <div class="property-enable"><el-checkbox v-model="property.config.enabled" :disabled="!supported(property)" @change="markDirty" /></div>
              <div class="property-name"><span v-if="property.is_required" class="required">*</span>{{ property.name }}：</div>
              <div class="property-control">
                <el-input v-if="property.type_v2 === 'string'" v-model.trim="property.config.attr_value" :placeholder="property.hint || '请输入'" @input="enableProperty(property)" />
                <el-select v-else-if="property.type_v2 === 'select_one'" v-model="property.config.attr_value" filterable clearable :allow-create="!!property.append_allowed" default-first-option :placeholder="property.hint || '请选择'" class="control-full" @change="enableProperty(property)">
                  <el-option v-for="option in options(property)" :key="option" :label="option" :value="option" />
                </el-select>
                <el-select v-else-if="property.type_v2 === 'select_many'" v-model="property.config.attr_value" multiple filterable clearable :allow-create="!!property.append_allowed" default-first-option :placeholder="property.hint || '请选择'" class="control-full" @change="enableProperty(property)">
                  <el-option v-for="option in options(property)" :key="option" :label="option" :value="option" />
                </el-select>
                <el-input-number v-else-if="['integer', 'decimal4'].includes(property.type_v2)" v-model="property.config.attr_value" :precision="property.type_v2 === 'integer' ? 0 : 4" :controls="false" :placeholder="property.hint || (property.type_v2 === 'integer' ? '请输入整数' : '最多4位小数')" class="control-full" @change="enableProperty(property)" />
                <el-input v-else-if="['integer_unit', 'decimal4_unit'].includes(property.type_v2)" v-model.trim="property.config.value" :placeholder="property.hint || '请输入数值'" @input="enableProperty(property)">
                  <el-select slot="append" v-model="property.config.unit" placeholder="单位" class="unit-select" @change="enableProperty(property)">
                    <el-option v-for="unit in options(property)" :key="unit" :label="unit" :value="unit" />
                  </el-select>
                </el-input>
                <el-alert v-else type="warning" title="该属性类型暂不支持配置" :closable="false" />
              </div>
              <div v-if="supported(property)" class="apply-mode">
                <el-radio-group v-model="property.config.apply_mode" @change="markDirty">
                  <el-radio label="force">强制使用</el-radio>
                  <el-radio label="unmatched_only">应用至未自动匹配</el-radio>
                </el-radio-group>
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'WechatCategoryAttributeEditor',
  props: {
    shopId: { type: Number, required: true },
    templateId: { type: [Number, String], default: 0 },
    globalMode: { type: Boolean, default: false },
    embedded: { type: Boolean, default: true },
    showTip: { type: Boolean, default: true },
    height: { type: String, default: '560px' }
  },
  data () {
    return {
      keyword: '',
      configured: 'all',
      categories: [],
      configuredCount: 0,
      categoriesLoading: false,
      propertiesLoading: false,
      saving: false,
      properties: [],
      propertyError: '',
      activeCategory: {},
      dirty: false,
      searchTimer: null,
      tabs: [{ value: 'all', label: '全部分类' }, { value: 'yes', label: '已配置' }, { value: 'no', label: '未配置' }]
    }
  },
  watch: {
    templateId () { this.initialize() },
    shopId () { this.initialize() },
    globalMode () { this.initialize() }
  },
  mounted () { this.initialize() },
  beforeDestroy () { if (this.searchTimer) clearTimeout(this.searchTimer) },
  methods: {
    ...mapActions({
      loadCategoriesAction: 'ucenter/getWechatMoveCategories',
      loadPropertiesAction: 'ucenter/getWechatMoveCategoryProperties',
      loadGlobalPropertiesAction: 'ucenter/getWechatMoveGlobalCategoryProperties',
      saveCategoryConfigAction: 'ucenter/saveWechatMoveCategoryConfig',
      saveGlobalCategoryConfigAction: 'ucenter/saveWechatMoveGlobalCategoryConfig'
    }),

    initialize () {
      this.keyword = ''
      this.configured = 'all'
      this.activeCategory = {}
      this.properties = []
      this.propertyError = ''
      this.setDirty(false)
      this.setActiveCategory({})
      this.loadCategories()
    },

    reload (keepCategory = true) {
      this.loadCategories(keepCategory)
    },

    save () {
      if (this.saving) return
      if (!this.activeCategory.category_id) {
        this.$message.warning('请先选择需要配置的类目')
        return
      }
      const attributes = this.properties.filter(property => property.config.enabled).map(property => ({
        enabled: true,
        attr_key: property.name,
        attr_value: property.config.attr_value,
        value: property.config.value,
        unit: property.config.unit,
        apply_mode: property.config.apply_mode
      }))
      if (this.properties.some(property => property.config.enabled && !this.supported(property))) {
        return this.$message.error('存在不支持的属性类型，无法保存')
      }
      this.saving = true
      this.$emit('saving-change', true)
      const action = this.globalMode ? this.saveGlobalCategoryConfigAction : this.saveCategoryConfigAction
      action({
        shopId: this.shopId,
        templateId: this.templateId,
        categoryId: this.activeCategory.category_id,
        data: { attributes },
        success: res => {
          this.saving = false
          this.$emit('saving-change', false)
          if (res.code !== 200) {
            this.$emit('save-error')
            return this.$message.error(res.msg || '保存失败')
          }
          this.setDirty(false)
          this.activeCategory.is_configured = attributes.length > 0
          this.$message.success(res.msg || '保存成功')
          this.loadCategories(true)
          this.$emit('saved')
        },
        fail: () => {
          this.saving = false
          this.$emit('saving-change', false)
          this.$emit('save-error')
          this.$message.error('保存失败，请稍后重试')
        }
      })
    },

    discardDirty () {
      this.setDirty(false)
    },

    queueLoadCategories () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.loadCategories, 300)
    },

    changeTab (value) {
      if (this.configured === value) return
      this.configured = value
      this.loadCategories()
    },

    loadCategories (keepCategory = false) {
      if (!this.globalMode && !this.templateId) return
      const activeId = keepCategory ? Number(this.activeCategory.category_id || 0) : 0
      this.categoriesLoading = true
      this.loadCategoriesAction({
        shopId: this.shopId,
        data: { template_id: this.templateId, keyword: this.keyword, configured: this.configured },
        success: res => {
          this.categoriesLoading = false
          if (res.code !== 200) return this.$message.error(res.msg || '获取类目失败')
          const data = res.data || {}
          this.categories = data.categories || []
          this.configuredCount = Number(data.configured_count || 0)
          if (activeId) {
            const active = this.categories.find(item => Number(item.category_id) === activeId)
            if (active) this.activeCategory = active
          }
        },
        fail: () => {
          this.categoriesLoading = false
          this.$message.error('获取类目失败，请稍后重试')
        }
      })
    },

    categoryPath (item) {
      const path = item && item.path_names
      if (Array.isArray(path)) return path.filter(Boolean).join('>')
      return String(path || (item || {}).name || '').replace(/\s*>\s*/g, '>')
    },

    selectCategory (category) {
      const load = () => {
        this.setActiveCategory(category)
        this.loadProperties()
      }
      if (!this.dirty) return load()
      this.$confirm('当前类目有未保存的修改，切换后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(load)
        .catch(() => {})
    },

    loadProperties () {
      this.properties = []
      this.propertyError = ''
      this.propertiesLoading = true
      this.setDirty(false)
      const action = this.globalMode ? this.loadGlobalPropertiesAction : this.loadPropertiesAction
      action({
        shopId: this.shopId,
        templateId: this.templateId,
        categoryId: this.activeCategory.category_id,
        success: res => {
          this.propertiesLoading = false
          if (res.code !== 200) {
            this.propertyError = res.msg || '获取属性规则失败'
            return
          }
          const saved = {}
          const config = (res.data || {}).config || {}
          ;(config.attributes || []).forEach(item => {
            saved[item.attr_key] = item
          })
          this.properties = ((res.data || {}).properties || []).map(property => this.decorateProperty(property, saved[property.name]))
        },
        fail: () => {
          this.propertiesLoading = false
          this.propertyError = '获取属性规则失败，请稍后重试'
        }
      })
    },

    decorateProperty (property, saved) {
      const config = { enabled: !!saved, attr_value: '', value: '', unit: '', apply_mode: (saved || {}).apply_mode || 'force' }
      if (saved) {
        if (property.type_v2 === 'select_many') config.attr_value = String(saved.attr_value || '').split(';').filter(Boolean)
        else if (['integer_unit', 'decimal4_unit'].includes(property.type_v2)) {
          const parts = String(saved.attr_value || '').trim().split(/\s+/)
          config.value = parts.shift() || ''
          config.unit = parts.join(' ')
        } else config.attr_value = saved.attr_value === null || saved.attr_value === undefined ? '' : saved.attr_value
      }
      return { ...property, config }
    },

    options (property) {
      return property.value === 'g/ml' ? ['g', 'ml'] : String(property.value || '').split(';').map(item => item.trim()).filter(Boolean)
    },

    supported (property) {
      return ['string', 'select_one', 'select_many', 'integer', 'decimal4', 'integer_unit', 'decimal4_unit'].includes(property.type_v2)
    },

    enableProperty (property) {
      if (this.supported(property)) property.config.enabled = true
      this.markDirty()
    },

    markDirty () {
      this.setDirty(true)
    },

    setActiveCategory (category) {
      this.activeCategory = category || {}
      this.$emit('category-change', Boolean(this.activeCategory.category_id))
    },

    setDirty (dirty) {
      const next = Boolean(dirty)
      if (this.dirty === next) return
      this.dirty = next
      this.$emit('dirty-change', next)
    }
  }
}
</script>

<style scoped>
.editor-tip { margin: 0 0 12px; color: #909399; font-size: 13px; line-height: 20px; }
.attribute-layout { display: flex; border: 1px solid #ebeef5; }
.category-panel { width: 34%; min-width: 330px; padding: 14px; border-right: 1px solid #ebeef5; box-sizing: border-box; }
.filter-tabs { display: flex; height: 44px; margin-top: 4px; border-bottom: 1px solid #dcdfe6; }
.filter-tabs button { position: relative; flex: 1; border: 0; background: transparent; color: #303133; font-size: 14px; font-weight: 600; cursor: pointer; }
.filter-tabs button.active { color: #409eff; }
.filter-tabs button.active::after { position: absolute; right: 18px; bottom: -1px; left: 18px; height: 3px; background: #409eff; content: ""; }
.category-list { height: calc(100% - 86px); margin: 0 -14px; overflow: auto; }
.category-item { display: flex; width: 100%; justify-content: space-between; gap: 8px; padding: 9px 14px; border: 0; background: transparent; color: #303133; cursor: pointer; font-size: 13px; text-align: left; }
.category-item:hover { background: #f5f7fa; }
.category-item.active { background: #ecf5ff; color: #409eff; }
.category-path { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.configured-icon { color: #67c23a; }
.property-panel { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.property-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; border-bottom: 1px solid #ebeef5; color: #606266; font-size: 13px; }
.category-id { margin-left: 8px; color: #909399; }
.property-list { flex: 1; overflow: auto; padding: 6px 0; }
.property-section-title { padding: 8px 14px; color: #909399; font-size: 12px; }
.property-row { display: grid; grid-template-columns: 26px 130px minmax(220px, 1fr) minmax(210px, 230px); gap: 10px; align-items: center; padding: 8px 14px; }
.property-row:hover { background: #fafafa; }
.property-enable { display: flex; align-items: center; justify-content: center; }
.property-name { color: #303133; font-size: 13px; text-align: left; }
.required { color: #f56c6c; }
.property-control { min-width: 0; }
.control-full { width: 100%; }
.unit-select { width: 90px; }
.apply-mode .el-radio { margin-right: 14px; }
@media (max-width: 1600px) {
  .category-panel { width: 32%; min-width: 300px; }
  .property-row { grid-template-columns: 26px 120px minmax(200px, 1fr) minmax(180px, 210px); }
}
</style>
